import React, {Suspense} from "react";

import IconHome from "../../icons/icon_home";
import IconClipboard from "../../icons/icon_clipboard";
import IconNotebook from "../../icons/icon_notebook";
import IconBug from "../../icons/icon_bug";
import IconLifebuoy from "../../icons/icon_lifebuoy";
import IconStats from "../../icons/icon_stats";
import IconRuler from "../../icons/icon_ruler";
import IconEarth from "../../icons/icon_earth";
import IconGantt from "../../icons/icon_gantt";
import IconCogs from "../../icons/icon_cogs";
import IconInfo from "../../icons/icon_info";

import SectionLoading from "./SectionLoading";
import TabbedSection from "./TabbedSection";

// These belonged to the three components TabbedSection replaced. They're still
// the stylesheets that size those sections, so they're imported here - the one
// place that still knows which sections exist.
import "../Bugs/bugs.css";
import "../Support/support.css";
import "../ProjectSettings/projectSettings.css";

// Every section and tab body is its own chunk, only fetched once its route is
// actually visited - the project shell (Sidebar, chrome) is the only thing
// that has to be in the initial bundle.
const Home = React.lazy(() => import("../Home/Home"));
const KanbanSection = React.lazy(() => import("../Kanban/KanbanSection"));
const SprintBacklog = React.lazy(() => import("../Backlog/SprintBacklog"));
const Burndown = React.lazy(() => import("../Burndown/Burndown"));
const Scope = React.lazy(() => import("../Scope/Scope"));
const Gantt = React.lazy(() => import("../Gantt/Gantt"));
const Design = React.lazy(() => import("../Design/Design"));
const About = React.lazy(() => import("./About"));

// Tab contents, for the sections whose tabs are a fixed set (see
// sectionPaths.js's SECTION_TABS, which holds their slugs and labels). Split
// across the two files because that one is a leaf that anything may import to
// build a URL, while this one pulls in the whole app.
const AddBug = React.lazy(() => import("../Bugs/AddBug"));
const ReviewBugs = React.lazy(() => import("../Bugs/ReviewBugs"));
const AddSupportTicket = React.lazy(() => import("../Support/AddSupportTicket"));
const SupportQueue = React.lazy(() => import("../Support/SupportQueue"));
const SupportFaq = React.lazy(() => import("../Support/SupportFaq"));
const Swimlanes = React.lazy(() => import("../ProjectSettings/Swimlanes"));
const SettingsTasks = React.lazy(() => import("../ProjectSettings/Tasks"));
const SettingsBugs = React.lazy(() => import("../ProjectSettings/Bugs"));
const SupportEmail = React.lazy(() => import("../ProjectSettings/SupportEmail"));
const Users = React.lazy(() => import("../ProjectSettings/Users"));
const SettingsImages = React.lazy(() => import("../ProjectSettings/Images"));
const Calendar = React.lazy(() => import("../ProjectSettings/Calendar"));
const ApiAccess = React.lazy(() => import("../ProjectSettings/ApiAccess"));

// Wraps a lazy component's element in the Suspense boundary its chunk load
// needs. A helper because every entry below needs one, not because the wrap
// itself is complex.
const lazy = (element) => <Suspense fallback={<SectionLoading/>}>{element}</Suspense>;

export const TAB_ELEMENTS = {
    bugs: {add: lazy(<AddBug/>), review: lazy(<ReviewBugs/>)},
    support: {new: lazy(<AddSupportTicket/>), queue: lazy(<SupportQueue/>), faq: lazy(<SupportFaq/>)},
    settings: {
        swimlanes: lazy(<Swimlanes/>), tasks: lazy(<SettingsTasks/>), bugs: lazy(<SettingsBugs/>),
        support: lazy(<SupportEmail/>), users: lazy(<Users/>), images: lazy(<SettingsImages/>),
        calendar: lazy(<Calendar/>), api: lazy(<ApiAccess/>),
    },
};

/**
 * Every section of a project, once.
 *
 * This list used to be written out twice - as a `switch` in pages/Project.js
 * deciding what to render, and as eleven hand-written buttons in Sidebar.js -
 * which is why the two had drifted: the sidebar offered an "About" button the
 * switch had no case for, so it rendered empty chrome. Both now read from here,
 * and AppRouter.js builds the route table from it as well, so a new section is
 * one entry rather than three edits in three files.
 *
 * Fields:
 *   slug         the URL segment, and the identity used everywhere else
 *   label, Icon  how the sidebar draws it
 *   permission   the VIEW_* key that has to be held to see it at all; null
 *                means everyone (see Sidebar.js's canView for the
 *                not-loaded-yet rule)
 *   contentClass the wrapper class the section's content sits in - Kanban and
 *                Backlog each need their own layout on top of the shared one
 *   element      what the route renders
 *
 * Project-level things a section needs (the "create sprint" dialog, whether
 * the viewed sprint is read-only) come from the layout's outlet context - see
 * pages/projectContext.js.
 *
 * URL spelling lives in sectionPaths.js, not here: this module imports every
 * section component, so anything that merely wants to build a link would drag
 * the whole app in behind it - and, worse, form an import cycle with the
 * components that build links themselves.
 */
export const SECTIONS = [
    {
        slug: "home", label: "Home", Icon: IconHome, permission: "VIEW_HOME",
        contentClass: "kanban-content", element: lazy(<Home/>),
    },
    {
        slug: "kanban", label: "Kanban", Icon: IconClipboard, permission: "VIEW_KANBAN",
        contentClass: "kanban-content kanban-page", element: lazy(<KanbanSection/>),
    },
    {
        slug: "backlog", label: "Backlog", Icon: IconNotebook, permission: "VIEW_BACKLOG",
        contentClass: "kanban-content sidebyside", element: lazy(<SprintBacklog/>),
    },
    {
        slug: "bugs", label: "Bugs", Icon: IconBug, permission: "VIEW_BUGS",
        contentClass: "kanban-content", element: <TabbedSection slug={"bugs"}/>,
    },
    {
        slug: "support", label: "Support", Icon: IconLifebuoy, permission: "VIEW_SUPPORT",
        contentClass: "kanban-content", element: <TabbedSection slug={"support"}/>,
    },
    {
        // Shares VIEW_BACKLOG rather than having a key of its own - a burndown
        // is a reading of the backlog's own numbers.
        slug: "burndown", label: "Burndown", Icon: IconStats, permission: "VIEW_BACKLOG",
        contentClass: "kanban-content", element: lazy(<Burndown/>),
    },
    {
        slug: "design", label: "Design", Icon: IconRuler, permission: "VIEW_DESIGN",
        contentClass: "kanban-content", element: lazy(<Design/>),
    },
    {
        slug: "scope", label: "Project Scope", Icon: IconEarth, permission: "VIEW_SCOPE",
        contentClass: "kanban-content", element: lazy(<Scope/>),
    },
    {
        slug: "gantt", label: "Roadmap", Icon: IconGantt, permission: "VIEW_GANTT",
        contentClass: "kanban-content", element: lazy(<Gantt/>),
    },
    {
        slug: "settings", label: "Project Settings", Icon: IconCogs, permission: "VIEW_PROJECT_SETTINGS",
        contentClass: "kanban-content", element: (
            <TabbedSection
                slug={"settings"}
                theme={"purple"}
                emptyLabel={"You don't have access to any project settings."}
            />
        ),
    },
    {
        slug: "about", label: "About", Icon: IconInfo, permission: null,
        contentClass: "kanban-content", element: lazy(<About/>),
    },
];

export const sectionFor = (slug) => SECTIONS.find((section) => section.slug === slug) || null;
